import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'

import { getAmplifyClient } from '@/services/amplifyClient'
import { createBlueprintMtn, createSectionMtn } from '@/services/graphql'
import {
  getProjectBlueprints,
  projectBlueprintsQueryKey,
  type ProjectBlueprintSummary,
  selectPrimaryProjectBlueprint,
} from '@/services/queries/blueprint'
import type { BlueprintInput, SectionInput } from '@/types'

import { createOptimisticBlueprintCacheEntry } from './blueprint.cache'
import { updateBlueprintSectionsCache } from './section'

type GraphqlClient = Pick<ReturnType<typeof getAmplifyClient>, 'graphql'>

export type NewProjectSectionInput = Omit<
  SectionInput,
  'blueprintSectionsId'
> & {
  blueprintSectionsId?: string
}

type ProvidedBlueprint = Pick<
  BlueprintInput,
  'id' | 'name' | 'projectBlueprintsId' | 'imageUrl'
>

export type CreateSectionWithPrimaryBlueprintVariables = {
  projectId: string
  sectionInput: NewProjectSectionInput
  blueprint?: ProvidedBlueprint | null
  blueprintName?: string
}

type CreateSectionWithPrimaryBlueprintOptions =
  CreateSectionWithPrimaryBlueprintVariables & {
    graphqlClient?: GraphqlClient
    idFactory?: () => string
  }

type CreateSectionWithPrimaryBlueprintResult = {
  blueprint: BlueprintInput
  createdBlueprint: boolean
  sectionInput: SectionInput
}

const toBlueprintInput = (
  blueprint: ProjectBlueprintSummary | ProvidedBlueprint,
  projectId: string,
): BlueprintInput => ({
  id: blueprint.id,
  name: blueprint.name ?? 'New Blueprint',
  projectBlueprintsId: blueprint.projectBlueprintsId ?? projectId,
  imageUrl: blueprint.imageUrl ?? undefined,
})

const getProvidedBlueprint = (
  blueprint: ProvidedBlueprint | null | undefined,
  projectId: string,
): BlueprintInput | null => {
  if (!blueprint?.id) return null
  if (
    blueprint.projectBlueprintsId &&
    blueprint.projectBlueprintsId !== projectId
  ) {
    return null
  }

  return toBlueprintInput(blueprint, projectId)
}

const assertGraphqlSuccess = <T>(
  result: GraphQLResult<T>,
  operation: string,
) => {
  if (!result.errors?.length) return

  throw new Error(
    result.errors
      .map((error) => error.message)
      .filter(Boolean)
      .join(', ') || `${operation} failed`,
  )
}

const createBlueprint = async (
  graphqlClient: GraphqlClient,
  input: BlueprintInput,
) => {
  const result = (await graphqlClient.graphql({
    query: createBlueprintMtn,
    variables: { input },
  })) as GraphQLResult<{ createBlueprint: { createdAt: string } }>

  assertGraphqlSuccess(result, 'createBlueprint')
}

const createSection = async (
  graphqlClient: GraphqlClient,
  input: SectionInput,
) => {
  const result = (await graphqlClient.graphql({
    query: createSectionMtn,
    variables: { input },
  })) as GraphQLResult<{ createSection: { createdAt: string } }>

  assertGraphqlSuccess(result, 'createSection')
}

export const createSectionWithPrimaryBlueprint = async ({
  projectId,
  sectionInput,
  blueprint,
  blueprintName = 'New Blueprint',
  graphqlClient = getAmplifyClient(),
  idFactory = uuid,
}: CreateSectionWithPrimaryBlueprintOptions): Promise<CreateSectionWithPrimaryBlueprintResult> => {
  if (!projectId || projectId === 'create') {
    throw new Error('A valid project id is required to create a section')
  }

  const projectBlueprints = await getProjectBlueprints(projectId, graphqlClient)
  const existingBlueprint = selectPrimaryProjectBlueprint(projectBlueprints)
  const providedBlueprint = getProvidedBlueprint(blueprint, projectId)

  const resolvedBlueprint = existingBlueprint
    ? toBlueprintInput(existingBlueprint, projectId)
    : providedBlueprint

  const createdBlueprint = !resolvedBlueprint
  const primaryBlueprint =
    resolvedBlueprint ??
    ({
      id: idFactory(),
      name: blueprintName,
      projectBlueprintsId: projectId,
    } satisfies BlueprintInput)

  if (createdBlueprint) {
    await createBlueprint(graphqlClient, primaryBlueprint)
  }

  const resolvedSectionInput: SectionInput = {
    ...sectionInput,
    blueprintSectionsId: primaryBlueprint.id,
  }

  await createSection(graphqlClient, resolvedSectionInput)

  return {
    blueprint: primaryBlueprint,
    createdBlueprint,
    sectionInput: resolvedSectionInput,
  }
}

export const useCreateSectionWithPrimaryBlueprint = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createSectionWithPrimaryBlueprint'],
    mutationFn: (variables: CreateSectionWithPrimaryBlueprintVariables) =>
      createSectionWithPrimaryBlueprint(variables),
    onSuccess: async (result, variables) => {
      if (result.createdBlueprint) {
        const previousBlueprint = queryClient.getQueryData([
          'blueprint',
          result.blueprint.id,
        ])
        queryClient.setQueryData(
          ['blueprint', result.blueprint.id],
          createOptimisticBlueprintCacheEntry(
            result.blueprint,
            previousBlueprint,
          ),
        )
      }

      await updateBlueprintSectionsCache(queryClient, {
        upsertType: 'create',
        input: result.sectionInput,
      })

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: projectBlueprintsQueryKey(variables.projectId),
        }),
        queryClient.invalidateQueries({
          queryKey: ['project', variables.projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['projects'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['blueprint', result.blueprint.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['section', result.sectionInput.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sections', variables.projectId],
        }),
      ])
    },
  })
}
